import { useQuery } from "@tanstack/react-query";
import { get } from "../services/apiClient";

export interface DashboardStats {
  totalBusinesses?: number;
  totalCentres?: number;
  totalProviders?: number;
  totalUsers?: number;
  totalCustomers?: number;
  pendingVerifications?: number;
  pendingReviews?: number;
  totalBookings?: number;
  activeBookings?: number;
  completedBookings?: number;
  totalRevenue?: number;
  [key: string]: number | undefined;
}

export const useDashboardStats = () =>
  useQuery<DashboardStats>({
    queryKey: ["dashboard", "stats"],
    queryFn: () => get<DashboardStats>("/dashboard/stats"),
  });

export type RevenueRange = "30d" | "90d" | "12m";
export type Granularity = "day" | "month";

export interface RevenueBucket {
  date: string;
  revenue: number;
  bookings: number;
}

export interface RevenueResult {
  range: RevenueRange;
  granularity: Granularity;
  buckets: RevenueBucket[];
}

export const useDashboardRevenue = (range: RevenueRange = "30d") =>
  useQuery<RevenueResult>({
    queryKey: ["dashboard", "revenue", range],
    queryFn: () => get<RevenueResult>("/dashboard/revenue", { range }),
  });

export type TopCentresMetric = "revenue" | "bookings";
export type TopCentresRange = "30d" | "90d" | "12m" | "all";

export interface TopCentreItem {
  centreId: string;
  name: string;
  city: string;
  value: number;
}

export interface TopCentresResult {
  metric: TopCentresMetric;
  range: TopCentresRange;
  items: TopCentreItem[];
}

interface TopCentresArgs {
  metric?: TopCentresMetric;
  range?: TopCentresRange;
  limit?: number;
}

export const useDashboardTopCentres = (args: TopCentresArgs = {}) => {
  const { metric = "revenue", range = "30d", limit = 5 } = args;
  return useQuery<TopCentresResult>({
    queryKey: ["dashboard", "top-centres", metric, range, limit],
    queryFn: () =>
      get<TopCentresResult>("/dashboard/top-centres", { metric, range, limit }),
  });
};
